'use client';

import React, { createContext, useContext, useState, useCallback } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

interface ToastContextType {
  toast: {
    success: (message: string, duration?: number) => void;
    error: (message: string, duration?: number) => void;
    warning: (message: string, duration?: number) => void;
    info: (message: string, duration?: number) => void;
  };
  toasts: Toast[];
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (message: string, type: ToastType, duration = 4000) => {
      const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newToast: Toast = { id, message, type, duration };

      setToasts((prev) => [...prev, newToast]);

      if (duration > 0) {
        setTimeout(() => {
          removeToast(id);
        }, duration);
      }
    },
    [removeToast]
  );

  const toast = React.useMemo(
    () => ({
      success: (message: string, duration?: number) => addToast(message, 'success', duration),
      error: (message: string, duration?: number) => addToast(message, 'error', duration),
      warning: (message: string, duration?: number) => addToast(message, 'warning', duration),
      info: (message: string, duration?: number) => addToast(message, 'info', duration),
    }),
    [addToast]
  );

  return (
    <ToastContext.Provider value={{ toast, toasts, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

function ToastContainer({
  toasts,
  removeToast,
}: {
  toasts: Toast[];
  removeToast: (id: string) => void;
}) {
  return (
    <div className="fixed top-6 right-6 z-[9999] flex flex-col gap-3 w-full max-w-sm pointer-events-none">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onClose={() => removeToast(t.id)} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onClose }: { toast: Toast; onClose: () => void }) {
  const { message, type } = toast;

  const config = {
    success: {
      bg: 'bg-emerald-50/90 border-emerald-500/30 text-emerald-950',
      icon: <CheckCircle className="text-emerald-600 shrink-0" size={18} />,
      title: 'Success',
    },
    error: {
      bg: 'bg-red-50/90 border-red-500/30 text-red-950',
      icon: <AlertCircle className="text-red-600 shrink-0" size={18} />,
      title: 'Error',
    },
    warning: {
      bg: 'bg-amber-50/90 border-amber-500/30 text-amber-950',
      icon: <AlertTriangle className="text-amber-600 shrink-0" size={18} />,
      title: 'Warning',
    },
    info: {
      bg: 'bg-blue-50/90 border-blue-500/30 text-blue-950',
      icon: <Info className="text-blue-600 shrink-0" size={18} />,
      title: 'Info',
    },
  }[type];

  return (
    <div
      className={`pointer-events-auto flex items-start gap-3 p-4 rounded-none border shadow-lg backdrop-blur-md transition-all duration-300 animate-in slide-in-from-right-8 fade-in-20 duration-300 ${config.bg}`}
      role="alert"
    >
      {config.icon}
      <div className="flex-1 space-y-0.5">
        <h5 className="text-[11px] font-black uppercase tracking-wider leading-none">
          {config.title}
        </h5>
        <p className="text-xs font-semibold leading-relaxed opacity-90">{message}</p>
      </div>
      <button
        onClick={onClose}
        className="text-slate-400 hover:text-slate-700 transition-colors shrink-0 p-0.5"
      >
        <X size={14} />
      </button>
    </div>
  );
}
